import { inject, Injectable, NgZone } from "@angular/core";
import { Data } from "@angular/router";
import { getAuth } from "firebase/auth";
import { firstValueFrom, of, Subscription } from "rxjs";
import { catchError, filter, take, timeout } from "rxjs/operators";
import { CheckoutFlowService, resolvePaymentVisitorId } from "src/app/services/checkout-flow.service";
import { PaymentService } from "src/app/services/payment.service";
import { SalesJourneyIdentityService } from "src/app/services/sales-journey-identity.service";
import { environment } from "src/environments/environment";
import { v4 as uuidv4 } from "uuid";

export interface AccountAddCardFlowHost {
  keys: Data;
  language: string;
  paymentMode: string;
  pathString: string;
  userId: string;
  checkOutComData?: Data;
  userData?: Data;
  coPurchaseDetails?: Data;
  preferredCurrency: string;
  preAuthAmount: number;
  paymentReason: string;
  isCheckoutComform: boolean;
  isButtonDisabled: boolean;
  showCheckOutComSpinner: boolean;
  errorMessage: string;
  checkoutFlowReady?: boolean;
  cardTokenizedCalled?: boolean;
  getCheckOutComResponseSubscription?: Subscription;
  getCookie?: (name: string) => string | null;
  onFlowReady?: () => void;
  onFlowPaymentCompleted?: () => void;
  sandbox: boolean;
}

@Injectable({ providedIn: "root" })
export class AccountAddCardCheckoutFlowService {
  private checkoutFlowService = inject(CheckoutFlowService);
  private paymentService = inject(PaymentService);
  private ngZone = inject(NgZone);
  private cachedDisplaySession: Data | null = null;
  private warmStartPromise: Promise<void> | null = null;
  private warmStartCacheKey = "";
  private salesJourneyIdentityService = inject(SalesJourneyIdentityService);
  unmount(): void {
    this.checkoutFlowService.unmount();
  }

  reset(): void {
    this.cachedDisplaySession = null;
    this.warmStartPromise = null;
    this.warmStartCacheKey = "";
  }

  /**
   * Invalidates only display-session warm-start state (single-use after pay).
   */
  clearCachedDisplaySession(): void {
    this.cachedDisplaySession = null;
    this.warmStartPromise = null;
    this.warmStartCacheKey = "";
  }

  /**
   * Full teardown for Account → Payment Methods add-card.
   * Clears cached display sessions (single-use after pay) and Flow DOM so a
   * second "Add Payment Method" visit can remount cleanly.
   */
  teardown(): void {
    this.reset();
    this.checkoutFlowService.resetCheckoutCardFrame();
    this.clearFlowContainerDom();
  }

  private clearFlowContainerDom(): void {
    if (typeof document === "undefined") {
      return;
    }
    document.querySelectorAll("#cko-flow-container").forEach((el) => {
      el.innerHTML = "";
    });
  }

  submitPayment(): void {
    this.checkoutFlowService.submitPayment();
  }

  isCheckoutLoadingMessage(message: string): boolean {
    return (message || "").trim().toLowerCase() === "loading secure card form...";
  }

  async prepareCheckoutSession(host: AccountAddCardFlowHost): Promise<void> {
    return this.warmStart(host);
  }

  hasCachedDisplaySession(host: AccountAddCardFlowHost): boolean {
    const cacheKey = `${host.pathString}:${host.paymentMode}:${host.preAuthAmount}`;
    return this.warmStartCacheKey === cacheKey && !!this.cachedDisplaySession;
  }

  private async warmStart(host: AccountAddCardFlowHost): Promise<void> {
    const cacheKey = `${host.pathString}:${host.paymentMode}:${host.preAuthAmount}`;
    if (this.warmStartCacheKey === cacheKey && this.cachedDisplaySession) {
      return;
    }

    if (this.warmStartPromise && this.warmStartCacheKey === cacheKey) {
      return this.warmStartPromise;
    }

    this.warmStartCacheKey = cacheKey;
    this.warmStartPromise = (async () => {
      await this.checkoutFlowService.preloadSdk();
      const paymentData = this.buildPaymentData(host);
      this.cachedDisplaySession = await this.createDisplaySession(host, paymentData);
    })();

    try {
      await this.warmStartPromise;
    } catch (error) {
      this.warmStartPromise = null;
      this.cachedDisplaySession = null;
      throw error;
    }
  }

  async mountAccountAddCardFlow(host: AccountAddCardFlowHost): Promise<void> {
    if (!host.keys?.publicKey) {
      throw new Error("Checkout publicKey is not configured");
    }

    host.isCheckoutComform = true;
    host.checkoutFlowReady = false;
    host.cardTokenizedCalled = false;
    host.errorMessage = "Loading secure card form...";
    host.showCheckOutComSpinner = true;
    host.isButtonDisabled = true;

    if (host.pathString) {
      this.paymentService.setcheckOutComDocId({ docId: String(host.pathString) });
    }

    if (host.userId) {
      this.paymentService.checkoutFlowUserId$.next(host.userId);
    }

    const paymentData = this.buildPaymentData(host);
    const componentOptions = this.checkoutFlowService.buildComponentOptionsFromPaymentData(
      host.keys,
      paymentData as Record<string, unknown>,
    );

    // Container clear only — CheckoutFlowService.mountForCardEntry unmounts itself.
    // Entry teardown already disposed any prior Flow instance.
    this.clearFlowContainerDom();

    if (!this.cachedDisplaySession) {
      const cacheKey = `${host.pathString}:${host.paymentMode}:${host.preAuthAmount}`;
      if (this.warmStartPromise && this.warmStartCacheKey === cacheKey) {
        await this.warmStartPromise;
      } else {
        await this.warmStart(host);
      }
    }

    // If warm-start finished without a session (cleared/consumed), create on mount.
    if (!this.cachedDisplaySession) {
      this.warmStartPromise = null;
      this.warmStartCacheKey = "";
    }

    await this.checkoutFlowService.mountForCardEntry({
      container: "#cko-flow-container",
      publicKey: host.keys.publicKey,
      environment: this.checkoutFlowService.getFlowEnvironment(host.paymentMode, host.keys.publicKey),
      locale: this.checkoutFlowService.mapLocale(host.language || "EN-GB"),
      componentOptions,
      paymentSession: this.cachedDisplaySession ?? undefined,
      createDisplaySession: this.cachedDisplaySession ? undefined : () => this.createDisplaySession(host, paymentData),
      onReady: () =>
        this.ngZone.run(() => {
          host.checkoutFlowReady = true;
          host.showCheckOutComSpinner = false;
          host.isButtonDisabled = false;
          if (this.isCheckoutLoadingMessage(host.errorMessage)) {
            host.errorMessage = "";
          }
          host.onFlowReady?.();
        }),
      onPaymentCompleted: (_self, paymentResponse) =>
        this.ngZone.run(() => {
          host.cardTokenizedCalled = true;
          host.showCheckOutComSpinner = true;
          host.isCheckoutComform = false;
          host.isButtonDisabled = true;
          // Display sessions are single-use after pay — drop session only.
          this.clearCachedDisplaySession();
          host.onFlowPaymentCompleted?.();

          const logId = host.pathString ? String(host.pathString) : "";
          const userId = host.userId ? String(host.userId) : "";
          const paymentId = paymentResponse?.id ?? paymentResponse?.payment_id ?? paymentResponse?.paymentId;
          if (!logId || !userId || !paymentId) {
            return;
          }

          this.redirectToCheckoutResponseHandler({
            status: "success",
            logId,
            userId,
            paymentId: String(paymentId),
            sandbox: host.paymentMode === "sandbox",
          });
        }),
      onError: (_self, error) =>
        this.ngZone.run(() => {
          const paymentId = error?.details?.paymentId;
          const logId = host.pathString ? String(host.pathString) : "";
          const userId = host.userId ? String(host.userId) : "";

          if (paymentId && logId && userId) {
            this.redirectToCheckoutResponseHandler({
              status: "fail",
              logId,
              userId,
              paymentId: String(paymentId),
              sandbox: host.paymentMode === "sandbox",
            });
            return;
          }

          host.errorMessage = error?.message || "Failed to load payment form";
          host.showCheckOutComSpinner = false;
          host.isButtonDisabled = true;
          host.onFlowReady?.();
        }),
    });
  }

  private redirectToCheckoutResponseHandler(options: {
    status: "success" | "fail";
    logId: string;
    userId: string;
    paymentId: string;
    sandbox: boolean;
  }): void {
    const params = new URLSearchParams({
      status: options.status,
      logId: options.logId,
      userId: options.userId,
      "cko-payment-id": options.paymentId,
    });
    if (options.sandbox) {
      params.set("sandbox", "true");
    }

    window.location.href = `${environment.onSuccessRedirectUrl}/${environment.cloudFunctions.checkoutFlowResponseHandler}?${params}`;
  }

  waitForPaymentConfirmation(
    host: AccountAddCardFlowHost,
    handlers: {
      onSuccess: (paymentResData: Data) => void;
      onFailure: () => void;
      onTimeout?: () => void;
    },
  ): Subscription {
    host.getCheckOutComResponseSubscription?.unsubscribe();

    const subscription = this.paymentService.getCheckOutComResponse$
      .pipe(
        filter((paymentResData: Data) => {
          const status = paymentResData?.payment_response?.payment_status;
          return status === "success" || status === "fail";
        }),
        take(1),
        timeout(120000),
        catchError(() => of(null)),
      )
      .subscribe((paymentResData: Data | null) => {
        this.ngZone.run(() => {
          host.showCheckOutComSpinner = false;

          if (!paymentResData?.payment_response) {
            handlers.onTimeout?.();
            handlers.onFailure();
            return;
          }

          if (paymentResData.payment_response.payment_status === "success") {
            handlers.onSuccess(paymentResData);
            return;
          }

          handlers.onFailure();
        });
      });

    host.getCheckOutComResponseSubscription = subscription;
    return subscription;
  }

  private async createDisplaySession(host: AccountAddCardFlowHost, paymentData: Data): Promise<Data> {
    const response = await firstValueFrom(
      this.checkoutFlowService.createFlowSession(host.paymentMode, paymentData as Record<string, unknown>),
    );
    const session = this.checkoutFlowService.extractPaymentSession(response as Data);
    if (!session) {
      throw new Error("Failed to create checkout flow display session");
    }
    return session;
  }

  private buildPaymentData(host: AccountAddCardFlowHost): Data {
    const purchaseDetail = host.coPurchaseDetails ?? {};
    const profile = host.checkOutComData ?? host.userData ?? {};
    const msn = profile?.msn;
    const countryCode = profile?.country_code;
    const userNumber = msn?.startsWith?.("+")
      ? msn.substring(countryCode ? String(countryCode).length + 1 : 3)
      : msn?.substring(2);
    const auth = getAuth();
    const TP1 = localStorage.getItem("TP1") || "";
    const TP2 = localStorage.getItem("TP2") || "";
    const TP3 = localStorage.getItem("TP3") || "";
    const T_ID = localStorage.getItem("T_ID") || "";
    const amount = host.preAuthAmount ?? purchaseDetail?.prize ?? purchaseDetail?.offers?.price ?? 0;

    const paymentData = {
      amount,
      currency: host.preferredCurrency,
      email: profile?.email || (msn ? msn + "@lottosocial.com" : ""),
      name: profile?.name,
      phone: msn,
      userId: host.userId,
      logId: host.pathString,
      customer_purchase: host.checkOutComData?.customer_purchase,
      msn: {
        country_code: `+${countryCode ?? ""}`,
        number: userNumber,
      },
      sub_status: "pre_auth",
      payment_reason: host.paymentReason,
      offer_id: purchaseDetail?.offers?.offerid ? purchaseDetail.offers.offerid : purchaseDetail?.offer_id,
      prosub_id: purchaseDetail?.prosub_id ? purchaseDetail.prosub_id : localStorage.getItem("prosub_id"),
      anonymous_id: auth?.currentUser?.isAnonymous ? auth.currentUser.uid : null,
      tp1: TP1 === "null" ? "" : TP1,
      tp2: TP2 === "null" ? "" : TP2,
      tp3: TP3 === "null" ? "" : TP3,
      t_id: T_ID === "null" ? "" : T_ID,
      Other: purchaseDetail.other
        ? purchaseDetail.other
        : localStorage.getItem("Other")
          ? localStorage.getItem("Other")
          : host.getCookie?.("Other")
            ? host.getCookie("Other")
            : "",
      value: purchaseDetail?.offers?.price,
      preferred_currency: {
        Value: purchaseDetail.offers?.int_price?.[host.preferredCurrency]
          ? purchaseDetail.offers.int_price[host.preferredCurrency]
          : purchaseDetail?.prize,
        currency: host.preferredCurrency,
      },
      p_type: purchaseDetail?.p_type ? purchaseDetail.p_type : "21",
      visitorid: resolvePaymentVisitorId(),
      requestid: uuidv4(),
      product_description: Array.isArray(purchaseDetail?.offers?.description)
        ? purchaseDetail?.offers?.description?.[0]
        : purchaseDetail?.offers?.description,
      return_url: window.location.href,
    };

    const paymentDataWithIdentity = this.salesJourneyIdentityService.applyIdentityEnvelope(paymentData, {
      userId: auth?.currentUser?.isAnonymous ? null : (host.userId ?? null),
      anonymousId: auth?.currentUser?.isAnonymous ? auth.currentUser.uid : null,
      // Omit customerId: resolve the actual SQL ID when available.
      msn,
      checkoutAttemptId: host.pathString ? String(host.pathString) : null,
    });

    return paymentDataWithIdentity;
  }
}
